import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import { FilterOption } from 'models/Filters';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import { getSortedOptionsFromStrings } from 'utils';
import {
  BossModeAccoutnsTable,
  CREATED_AT_FILTER_KEY,
  EMAIL_FILTER_KEY,
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  NAME_FILTER_KEY,
  PROMOTED_AT_FILTER_KEY,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from '../components/BossModeTable/components/data';
import useMultiSort from 'hooks/useMultiSort';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { TIMEFRAME } from 'dateUtils';

export default function useBossModeAccountsFilters() {
  const { t } = useTranslation();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const createSelectOptions = useCreateSelectOptions();

  const getNameOptions = useCallback(async (searchString: string) => {
    const { data } = await api.portfolio.admin.getBossModeNameOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const getEmailOptions = useCallback(async (searchString: string) => {
    const { data } = await api.portfolio.admin.getBossModeEmailOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const nameFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('name'),
        NAME_FILTER_KEY,
        NAME_FILTER_KEY,
        getNameOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getNameOptions, t],
  );

  const emailFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('email'),
        EMAIL_FILTER_KEY,
        EMAIL_FILTER_KEY,
        getEmailOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getEmailOptions, t],
  );

  const timeframeOptions = useMemo(
    () => createSelectOptions(Object.values(TIMEFRAME)),
    [createSelectOptions],
  );

  const createdAtFilterOption = useMemo<FilterOption>(
    () => ({
      value: CREATED_AT_FILTER_KEY,
      label: t('joinedOn'),
      shouldSortOptions: false,
      options: timeframeOptions,
    }),
    [timeframeOptions, t],
  );

  const promotedAtFilterOption = useMemo<FilterOption>(
    () => ({
      value: PROMOTED_AT_FILTER_KEY,
      label: t('promotedOn'),
      shouldSortOptions: false,
      options: timeframeOptions,
    }),
    [timeframeOptions, t],
  );

  const filterOptions = useMemo(
    () => [
      nameFilterOption,
      emailFilterOption,
      createdAtFilterOption,
      promotedAtFilterOption,
    ],
    [
      nameFilterOption,
      emailFilterOption,
      createdAtFilterOption,
      promotedAtFilterOption,
    ],
  );

  const {
    multiSortValues: translatedSortOptions,
    multiSortDirectionTranslation,
  } = useMultiSort(
    TABLE_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () =>
      ({
        firstName: {
          options: nameFilterOption,
        },
        email: {
          options: emailFilterOption,
        },
        createdAt: {
          options: createdAtFilterOption,
          sortDirectionTranslation:
            multiSortDirectionTranslation[SORTING_VALUES.createdAt],
        },
        promotedAt: {
          options: promotedAtFilterOption,
          sortDirectionTranslation:
            multiSortDirectionTranslation[SORTING_VALUES.promotedAt],
        },
      } as Partial<Record<keyof BossModeAccoutnsTable, ColumnFilterOption>>),
    [
      nameFilterOption,
      emailFilterOption,
      createdAtFilterOption,
      promotedAtFilterOption,
      multiSortDirectionTranslation,
    ],
  );

  return {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  };
}
